// Mobile flight controls, ported from the tech-week condor (landing/touch.ts there): a
// joystick under one thumb that holds the same four flight keys the keyboard does (so the
// flight and the flock's reporting see no difference between a key and a drag), and a
// spring-loaded throttle lever under the other that walks the speed from cruise up to the top
// speed, then eases back to cruise when the thumb lifts. Here they take their elements and
// the engine's key state as arguments instead of reading them off globals.

/**
 * Lever position — 0 resting at the bottom, 1 pushed to the top — for a thumb at viewport `y`,
 * on a track `height` tall whose top edge is at `top` and whose knob is `knob` tall. The knob's
 * centre follows the thumb, so the ends are half a knob inside the track; past either one the
 * lever pins there.
 */
export function throttleAt(y: number, top: number, height: number, knob: number): number {
  const travel = height - knob;
  if (travel <= 0) return 0;
  const idle = top + height - knob / 2; // where the knob's centre sits with the lever down
  return Math.min(1, Math.max(0, (idle - y) / travel));
}

/** Steering: a relative drag from the base's centre, held as the four flight keys. Returns an
 *  unbind function. */
export function wireJoystick(base: HTMLElement, knob: HTMLElement, keys: Record<string, boolean>) {
  const R = 34; // px the knob travels before it pins to the base's edge (purely visual)
  const DEAD = R * 0.2;
  let pointerId: number | null = null;
  let originX = 0;
  let originY = 0;

  const setKeys = (dx: number, dy: number) => {
    keys.KeyA = dx < -DEAD;
    keys.KeyD = dx > DEAD;
    keys.KeyW = dy < -DEAD;
    keys.KeyS = dy > DEAD;
  };

  const move = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    const dx = e.clientX - originX;
    const dy = e.clientY - originY;
    const dist = Math.hypot(dx, dy);
    const clamped = Math.min(dist, R);
    const angle = Math.atan2(dy, dx);
    knob.style.transform = dist > DEAD ? `translate(${Math.cos(angle) * clamped}px, ${Math.sin(angle) * clamped}px)` : '';
    setKeys(dx, dy);
  };

  const end = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    pointerId = null;
    keys.KeyA = keys.KeyD = keys.KeyW = keys.KeyS = false;
    knob.style.transform = '';
  };

  const down = (e: PointerEvent) => {
    if (pointerId !== null) return;
    e.preventDefault();
    pointerId = e.pointerId;
    const r = base.getBoundingClientRect();
    originX = r.left + r.width / 2;
    originY = r.top + r.height / 2;
    base.setPointerCapture(e.pointerId);
    move(e);
  };

  base.addEventListener('pointerdown', down);
  base.addEventListener('pointermove', move);
  base.addEventListener('pointerup', end);
  base.addEventListener('pointercancel', end);
  return () => {
    base.removeEventListener('pointerdown', down);
    base.removeEventListener('pointermove', move);
    base.removeEventListener('pointerup', end);
    base.removeEventListener('pointercancel', end);
  };
}

/** The throttle lever: the knob follows the thumb up the track, and springs back on release.
 *  `--t` on the lever (0…1) is what the CSS fills and heats with. Returns an unbind function. */
export function wireThrottle(lever: HTMLElement, track: HTMLElement, knob: HTMLElement, set: (v: number) => void) {
  const SPRING_MS = 180;
  let pointerId: number | null = null;
  let value = 0;
  let spring = 0;

  const apply = (v: number) => {
    value = v;
    lever.style.setProperty('--t', v.toFixed(3));
    knob.style.transform = `translateY(${-v * (track.clientHeight - knob.offsetHeight)}px)`;
    set(v);
  };

  const follow = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    const r = track.getBoundingClientRect();
    apply(throttleAt(e.clientY, r.top + track.clientTop, track.clientHeight, knob.offsetHeight));
  };

  const end = (e: PointerEvent) => {
    if (e.pointerId !== pointerId) return;
    pointerId = null;
    const from = value;
    const t0 = performance.now();
    const step = () => {
      const k = Math.min(1, (performance.now() - t0) / SPRING_MS);
      apply(from * (1 - k) ** 2); // leaves the top at once, settles softly at the bottom
      if (k < 1) spring = requestAnimationFrame(step);
    };
    spring = requestAnimationFrame(step);
  };

  const down = (e: PointerEvent) => {
    if (pointerId !== null) return;
    e.preventDefault();
    pointerId = e.pointerId;
    cancelAnimationFrame(spring);
    lever.setPointerCapture(e.pointerId);
    follow(e);
  };

  lever.addEventListener('pointerdown', down);
  lever.addEventListener('pointermove', follow);
  lever.addEventListener('pointerup', end);
  lever.addEventListener('pointercancel', end);
  return () => {
    cancelAnimationFrame(spring);
    lever.removeEventListener('pointerdown', down);
    lever.removeEventListener('pointermove', follow);
    lever.removeEventListener('pointerup', end);
    lever.removeEventListener('pointercancel', end);
    set(0);
  };
}
